import type { CaptionCue, ImageClip, ReelProject } from '@/types';
import { computeFitRect, resolveCaptionBox, resolveWatermarkPosition } from './geometry';
import { drawLineWithWords, type Canvas2DContext } from './text';
import { wrapCueLines } from '../captionTiming';

export interface DrawFrameParams {
  ctx: Canvas2DContext;
  canvasWidth: number;
  canvasHeight: number;
  /** Current playback time in seconds, in the project's absolute timeline. */
  time: number;
  project: ReelProject;
  /** Decoded bitmaps for every ImageClip.mediaId used by the project. */
  imageBitmaps: Map<string, ImageBitmap>;
  watermarkBitmap: ImageBitmap | null;
  captionCues: CaptionCue[];
}

interface ActiveImageState {
  current: ImageClip | null;
  previous: ImageClip | null;
  /** 0..1 progress through the transition window at the start of `current`. */
  transitionProgress: number;
  /** 0..1 progress through `current`'s own on-screen duration (for Ken Burns). */
  clipProgress: number;
}

/** Draws a single frame of the Reel at `time` onto `ctx`. Pure & side-effect free besides the draw. */
export function drawFrame(params: DrawFrameParams): void {
  const { ctx, canvasWidth: W, canvasHeight: H, project, time } = params;

  ctx.save();
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, W, H);

  const activeImage = resolveActiveImage(project.images, time);
  drawImageLayer(params, activeImage);
  if (project.captions.enabled) drawCaptionsLayer(params);
  if (project.watermark.enabled) drawWatermarkLayer(params);

  ctx.restore();
}

// ---------------------------------------------------------------------------
// Image + transitions + Ken Burns
// ---------------------------------------------------------------------------

function resolveActiveImage(images: ImageClip[], time: number): ActiveImageState {
  const sorted = [...images].sort((a, b) => a.order - b.order);
  let cursor = 0;
  for (let i = 0; i < sorted.length; i++) {
    const clip = sorted[i];
    const start = cursor;
    const end = cursor + Math.max(0.1, clip.duration);
    if (time >= start && time < end) {
      const clipProgress = clamp01((time - start) / Math.max(0.001, end - start));
      const transitionDuration = Math.min(clip.transitionDuration, clip.duration / 2);
      const transitionProgress =
        transitionDuration > 0 ? clamp01((time - start) / transitionDuration) : 1;
      return {
        current: clip,
        previous: i > 0 ? sorted[i - 1] : null,
        transitionProgress,
        clipProgress,
      };
    }
    cursor = end;
  }
  const last = sorted[sorted.length - 1] ?? null;
  return { current: last, previous: null, transitionProgress: 1, clipProgress: 1 };
}

function drawImageLayer(params: DrawFrameParams, active: ActiveImageState): void {
  const { ctx, canvasWidth: W, canvasHeight: H, imageBitmaps } = params;
  if (!active.current) return;

  const bitmap = imageBitmaps.get(active.current.mediaId);
  if (!bitmap) return;

  const prevBitmap = active.previous ? imageBitmaps.get(active.previous.mediaId) : null;
  const t = active.transitionProgress;
  const transition = active.current.transitionIn;

  // Draw the outgoing (previous) frame first, when relevant, so the incoming
  // frame can be composited over it.
  if (prevBitmap && t < 1 && (transition === 'crossfade' || transition === 'fade')) {
    drawSingleImage(ctx, prevBitmap, active.previous!, W, H, 1, active.clipProgress, 0, 0);
  }

  switch (transition) {
    case 'none':
      drawSingleImage(ctx, bitmap, active.current, W, H, 1, active.clipProgress, 0, 0);
      break;
    case 'fade':
    case 'crossfade':
      drawSingleImage(ctx, bitmap, active.current, W, H, t, active.clipProgress, 0, 0);
      break;
    case 'slide': {
      const offsetX = (1 - t) * W;
      drawSingleImage(ctx, bitmap, active.current, W, H, 1, active.clipProgress, offsetX, 0);
      break;
    }
    case 'zoom': {
      const scaleBoost = 1 + (1 - t) * 0.18;
      drawSingleImage(ctx, bitmap, active.current, W, H, t, active.clipProgress, 0, 0, scaleBoost);
      break;
    }
    default:
      drawSingleImage(ctx, bitmap, active.current, W, H, 1, active.clipProgress, 0, 0);
  }
}

function drawSingleImage(
  ctx: Canvas2DContext,
  bitmap: ImageBitmap,
  clip: ImageClip,
  W: number,
  H: number,
  alpha: number,
  clipProgress: number,
  offsetX: number,
  offsetY: number,
  extraScale = 1
): void {
  const rect = computeFitRect(bitmap.width, bitmap.height, W, H, clip.fit);

  if (clip.fit === 'contain') {
    ctx.save();
    ctx.fillStyle = clip.backgroundColor || '#000000';
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }

  const kb = clip.kenBurns;
  let scale = extraScale;
  let focusX = 0.5;
  let focusY = 0.5;
  if (kb?.enabled) {
    const kbScale = 1 + (kb.endScale - 1) * clipProgress;
    scale *= kbScale;
    focusX = kb.startFocus.x + (kb.endFocus.x - kb.startFocus.x) * clipProgress;
    focusY = kb.startFocus.y + (kb.endFocus.y - kb.startFocus.y) * clipProgress;
  }

  const centerX = rect.x + rect.width / 2 + offsetX + (focusX - 0.5) * rect.width * 0.4;
  const centerY = rect.y + rect.height / 2 + offsetY + (focusY - 0.5) * rect.height * 0.4;

  ctx.save();
  ctx.globalAlpha = clamp01(alpha);
  ctx.translate(centerX, centerY);
  ctx.scale(scale, scale);
  ctx.drawImage(bitmap, -rect.width / 2, -rect.height / 2, rect.width, rect.height);
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Captions
// ---------------------------------------------------------------------------

function drawCaptionsLayer(params: DrawFrameParams): void {
  const { ctx, canvasWidth: W, canvasHeight: H, project, time, captionCues } = params;
  const settings = project.captions;
  const cue = captionCues.find((c) => time >= c.startSeconds && time <= c.endSeconds);
  if (!cue) return;

  const box = resolveCaptionBox(settings.position, settings.customRect, settings.widthPercent, settings.safeMarginPercent, W, H);
  const lines = wrapCueLines(cue.text, settings.maxCharsPerLine, settings.maxLines);

  ctx.save();
  ctx.font = `${settings.fontWeight} ${settings.fontSizePx}px "${settings.fontFamily}", sans-serif`;
  ctx.textBaseline = 'middle';

  const timeSinceCueStart = time - cue.startSeconds;
  const animAlpha = resolveAnimationAlpha(settings.animation, timeSinceCueStart);
  const animOffsetY = resolveAnimationOffsetY(settings.animation, timeSinceCueStart, H);

  if (settings.backgroundOpacity > 0) {
    ctx.save();
    ctx.globalAlpha = settings.backgroundOpacity * animAlpha;
    ctx.fillStyle = settings.backgroundColor;
    ctx.fillRect(box.x, box.y + animOffsetY, box.width, box.height);
    ctx.restore();
  }

  const lineHeightPx = settings.fontSizePx * settings.lineHeight;
  const totalTextHeight = lineHeightPx * lines.length;
  let cursorY = box.y + box.height / 2 - totalTextHeight / 2 + lineHeightPx / 2 + animOffsetY;

  const globalWordIndex = { i: 0 };
  for (const line of lines) {
    const lineWordCount = line.split(/\s+/).filter(Boolean).length;
    const cueWords = cue.words.slice(globalWordIndex.i, globalWordIndex.i + lineWordCount);
    globalWordIndex.i += lineWordCount;

    const segments = line
      .split(/\s+/)
      .filter(Boolean)
      .map((text, idx) => {
        const wordTiming = cueWords[idx];
        return buildWordSegment(text, wordTiming, settings, time, animAlpha);
      });

    ctx.globalAlpha = animAlpha;
    drawLineWithWords(
      ctx,
      segments,
      box.x,
      box.x + box.width,
      cursorY,
      settings.alignment,
      settings.letterSpacingPx,
      settings.outlineColor,
      settings.outlineWidthPx,
      settings.textColor
    );
    cursorY += lineHeightPx;
  }

  ctx.restore();
}

function buildWordSegment(
  text: string,
  wordTiming: CaptionCue['words'][number] | undefined,
  settings: ReelProject['captions'],
  time: number,
  baseAlpha: number
) {
  const isActive = wordTiming ? time >= wordTiming.startSeconds && time <= wordTiming.endSeconds : false;

  if (settings.animation === 'word-highlight') {
    return { text, color: isActive ? settings.activeWordColor : settings.textColor };
  }
  if (settings.animation === 'current-word-emphasis') {
    return { text, color: isActive ? settings.activeWordColor : settings.textColor, scale: isActive ? 1.12 : 1 };
  }
  void baseAlpha;
  return { text, color: settings.textColor };
}

function resolveAnimationAlpha(animation: ReelProject['captions']['animation'], t: number): number {
  const rampDuration = 0.22;
  if (animation === 'fade-in' || animation === 'pop') return clamp01(t / rampDuration);
  return 1;
}

function resolveAnimationOffsetY(animation: ReelProject['captions']['animation'], t: number, canvasHeight: number): number {
  if (animation === 'slide-up') {
    const rampDuration = 0.28;
    const progress = clamp01(t / rampDuration);
    return (1 - progress) * canvasHeight * 0.04;
  }
  return 0;
}

// ---------------------------------------------------------------------------
// Watermark
// ---------------------------------------------------------------------------

function drawWatermarkLayer(params: DrawFrameParams): void {
  const { ctx, canvasWidth: W, canvasHeight: H, project, time, watermarkBitmap } = params;
  const wm = project.watermark;
  if (!watermarkBitmap) return;

  const start = wm.alwaysVisible ? 0 : wm.startAt;
  const end = wm.alwaysVisible ? Infinity : wm.endAt ?? Infinity;
  if (time < start || time > end) return;

  const width = (wm.widthPercent / 100) * W;
  const height = width * (watermarkBitmap.height / watermarkBitmap.width);
  const { x, y } = resolveWatermarkPosition(wm.positionPreset, null, width, height, W, H, wm.offsetXPercent, wm.offsetYPercent);

  ctx.save();
  ctx.globalAlpha = wm.opacity;
  ctx.drawImage(watermarkBitmap, x, y, width, height);
  ctx.restore();
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}
